/**
 * «Perché» › «Non inviate» — the need a replenishment run left without a line, and what it lost to
 * (owner request 2026-10-24, levels 1 and 2; platform ADR 6207, `docs/adr/6005` amendment).
 *
 * Three reads of the pinned client:
 *
 *   - `GET …/report/replenishment/uncovered/by-store-article` — one row per store × article with an
 *     unserved need: the pairs not sent, the cells, the cells per reason, and the run's totals;
 *   - `GET …/report/replenishment/cells?store_key=&article_key=` — every size of the chosen pair, the
 *     sizes that were sent or needed nothing included (drawn greyed), with the evidence per size;
 *   - `GET …/report/replenishment/uncovered?unserved_need_only=true` — the files' rows, one per size
 *     not sent, with the same evidence.
 *
 * **The evidence is the platform's, worded here.** For `not_selected_margin` the expected margin per
 * pair of the store's presentation block beside the last store the same DC stock served, and the
 * store's place; for `not_selected_budget` the budget, what was spent when the store's turn came and
 * the store's block; for `dc_pool_exhausted` (and `dc_pool_empty` where the DC-stock pass cut the size)
 * the DC's stock of the size, what was left and how many stores had it first; for an article the run
 * never evaluated (`run_refused`, and `dc_pool_empty` where it lacked more than stock — platform ADR
 * 6216) the inputs it lacked and the DC's stock of the article. Every other reason has none, and the
 * sentence is absent rather than invented. Money is formatted in the platform's currency. Pure, apart
 * from the export resolver at the end.
 *
 * **`run_refused` is not a refused run** (owner, 2026-10-25): the run did not evaluate the article
 * because an input was missing, so the reason reads «Not evaluated: missing <inputs>» wherever the
 * platform names the inputs ({@link notSentReasonWords}). Where the DC held none of the article the
 * platform says `dc_pool_empty` first, and the missing inputs are the secondary note.
 */

import { collectPages } from '@/api';
import { uncoveredReport, type CellReportRow, type UncoveredReportRow } from '@/api/generated';
import type { ExportRequest } from '@/export';
import { i18next } from '@/i18n';
import { formatCurrency, formatInteger, formatNumber } from '@/lib/format';
import { scalesMix, sizeFilterValue, sizeName, sizeSortValue, tableRequest, type DataColumn } from '@/planning';

import type { ExportScope } from '../manifest';
import type { Translate } from './export-provenance';
import { NAME_HEADER_KEY } from './run-names';
import { primePullReasonLabels } from './network-health/pull-reasons';
import { cellOutcomeWords, sentences, type Sentences } from './run-cells';
import { readScope } from './scope';

/** The figures one uncovered reason was decided on, as either read publishes them. */
export type Evidence = Pick<
  UncoveredReportRow,
  | 'evidence_kind' | 'evidence_currency_code' | 'rank_position' | 'candidate_count' | 'selected_count'
  | 'budget_units' | 'spent_units' | 'cost_units' | 'pool_units' | 'left_units' | 'ahead_count'
  | 'missing_inputs'
> & {
  /** A number on the uncovered report, a decimal string on the cells read: both read as a number. */
  readonly margin_per_pair?: number | string | null;
  readonly cutoff_margin_per_pair?: number | string | null;
};

/** The evidence kinds the platform writes (its `planning.replenishment.evidence`). */
export const EVIDENCE_MARGIN = 'margin';
export const EVIDENCE_BUDGET = 'budget';
export const EVIDENCE_DC_POOL = 'dc_pool';
/** Platform ADR 6216: the article was never evaluated — the inputs it lacked, and the DC's stock of it. */
export const EVIDENCE_NOT_POSED = 'not_posed';

/** The platform's reasons an article the run never evaluated carries (its `uncovered.py`). */
const RUN_REFUSED = 'run_refused';
const DC_POOL_EMPTY = 'dc_pool_empty';

/**
 * The input names the platform gives what an article lacked (`assemble._option_gaps`), as a literal
 * table so every key is a reference the catalogue check sees. A name this build has no words for — an
 * estate-wide input, an engine refusal code — is shown as the platform's name, never guessed.
 */
export const INPUT_KEYS: Readonly<Record<string, string>> = {
  objective_terms: 'inseason.notSent.input.objectiveTerms',
  scales: 'inseason.notSent.input.scales',
  size_curves: 'inseason.notSent.input.sizeCurves',
  supply: 'inseason.notSent.input.supply',
  demand: 'inseason.notSent.input.demand',
  doors: 'inseason.notSent.input.doors',
};

/** The platform's `missing_inputs` (sorted, comma-separated) as names; none for `null` or empty. Pure. */
export function missingInputs(value: string | null | undefined): readonly string[] {
  if (value === null || value === undefined) return [];
  return value.split(',').map((name) => name.trim()).filter((name) => name !== '');
}

/** The missing inputs in words, as one list in the reader's language («size scale and size curves»); `null` for none. */
export function missingInputsWords(value: string | null | undefined, t: Translate, language: string): string | null {
  const names = missingInputs(value);
  if (names.length === 0) return null;
  const words = names.map((name) => {
    const key = INPUT_KEYS[name];
    if (key === undefined) return name;
    const text = t(key);
    return text === '' || text === key ? name : text;
  });
  return new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(words);
}

/**
 * A size's reason in words: `run_refused` with the inputs the platform recorded reads «Not evaluated:
 * missing size curves» (platform ADR 6216); every other reason, and `run_refused` from a run that
 * recorded none, is the run-cells words ({@link cellOutcomeWords}).
 */
export function notSentReasonWords(
  code: string, missing: string | null | undefined, t: Translate, language: string, position: number | null, weeks: number | null,
): string {
  if (code === RUN_REFUSED) {
    const inputs = missingInputsWords(missing, t, language);
    if (inputs !== null) return t('inseason.notSent.notEvaluated', { inputs });
  }
  return cellOutcomeWords(code, t, position, weeks);
}

function numeric(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function whole(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : value;
}

/**
 * The evidence in one sentence — «Margine atteso per paio −4.200 COP contro una soglia di 5.100 COP
 * (ultimo paio servito dallo stesso stock CD); posizione 1.312 su 1.250 selezionati». `null` where the
 * platform states none, or where a figure the sentence needs is missing: never a sentence with a hole.
 * `reason` is the size's uncovered reason: for an article never evaluated it decides whether the
 * sentence leads with the empty warehouse (`dc_pool_empty`) or with the input that was missing.
 */
export function evidenceSentence(evidence: Evidence, t: Translate, language: string, reason?: string | null): string | null {
  const count = (value: number): string => formatInteger(value, language);
  switch (evidence.evidence_kind) {
    case EVIDENCE_MARGIN: {
      const margin = numeric(evidence.margin_per_pair);
      const currency = evidence.evidence_currency_code ?? null;
      const rank = whole(evidence.rank_position);
      const selected = whole(evidence.selected_count);
      if (margin === null || currency === null || rank === null || selected === null) return null;
      const money = (value: number): string => formatCurrency(value, currency, language);
      const cutoff = numeric(evidence.cutoff_margin_per_pair);
      return cutoff === null
        ? t('inseason.notSent.evidence.marginNoCutoff', { margin: money(margin), rank: count(rank), candidates: count(whole(evidence.candidate_count) ?? rank) })
        : t('inseason.notSent.evidence.margin', { margin: money(margin), cutoff: money(cutoff), rank: count(rank), selected: count(selected) });
    }
    case EVIDENCE_BUDGET: {
      const budget = whole(evidence.budget_units);
      const spent = whole(evidence.spent_units);
      const cost = whole(evidence.cost_units);
      const rank = whole(evidence.rank_position);
      const selected = whole(evidence.selected_count);
      if (budget === null || spent === null || cost === null || rank === null || selected === null) return null;
      return t('inseason.notSent.evidence.budget', {
        budget: count(budget), spent: count(spent), remaining: count(Math.max(budget - spent, 0)), cost: count(cost), rank: count(rank), selected: count(selected),
      });
    }
    case EVIDENCE_DC_POOL: {
      const pool = whole(evidence.pool_units);
      const left = whole(evidence.left_units);
      const ahead = whole(evidence.ahead_count);
      if (pool === null || left === null || ahead === null) return null;
      return t('inseason.notSent.evidence.pool', { pool: count(pool), remaining: count(left), ahead });
    }
    case EVIDENCE_NOT_POSED: {
      const inputs = missingInputsWords(evidence.missing_inputs, t, language);
      if (inputs === null) return null;
      const pool = whole(evidence.pool_units);
      // The warehouse first (owner, 2026-10-25): with no stock, the missing input is the secondary note.
      if (reason === DC_POOL_EMPTY || (pool !== null && pool <= 0)) {
        return pool === null
          ? t('inseason.notSent.evidence.notPosedEmptyBare', { inputs })
          : t('inseason.notSent.evidence.notPosedEmpty', { pool: count(pool), inputs });
      }
      return pool === null
        ? t('inseason.notSent.evidence.notPosedBare', { inputs })
        : t('inseason.notSent.evidence.notPosed', { pool: count(pool), inputs });
    }
    default:
      return null;
  }
}

/** The uncovered reasons a need left without a line can carry (the platform's `UNSERVED_NEED_REASONS`' complement). */
const NOT_A_NEED = new Set(['need_zero_in_transit', 'need_below_one_unit', 'forecast_horizon_short', 'demand_not_forecast', 'proposal_line_written']);

/**
 * Whether a size of the chosen pair is one the run left unsent: a need of a pair or more and no line.
 * Every other size — sent, needing nothing, not measured — is drawn greyed beside them.
 */
export function isNotSent(cell: Pick<CellReportRow, 'outcome' | 'need_units'>): boolean {
  return !NOT_A_NEED.has(cell.outcome) && cell.need_units !== null && cell.need_units > 0
    && !cell.outcome.startsWith('size_without_forecast') && cell.outcome !== 'position_not_reported' && cell.outcome !== 'not_recorded_by_run';
}

/**
 * Why a size not sent got nothing, in one sentence with its own figures — the cells file's own words
 * (`./run-cells.ts`: «Fabbisogno di 1 paia (obiettivo 1 paia meno posizione 0 paia), nessuna proposta.
 * Motivo: …»). A figure the row lacks is left out, never made up.
 */
export function notSentExplanation(
  row: Pick<UncoveredReportRow, 'need_units' | 'target_units' | 'committed_units' | 'on_hand_units'>,
  reason: string, language: string, say: Sentences,
): string {
  const pairs = (value: number): string => formatNumber(value, language);
  const target = row.target_units ?? null;
  const committed = row.committed_units ?? null;
  if (row.need_units === null || row.need_units <= 0 || target === null || committed === null) {
    return say('inseason.runCells.explain.stoppedBare', { reason });
  }
  const position = row.on_hand_units === null
    ? say('inseason.runCells.explain.positionBare', { position: pairs(committed) })
    : say('inseason.runCells.explain.position', { position: pairs(committed), onHand: pairs(row.on_hand_units), inbound: pairs(committed - row.on_hand_units) });
  return say('inseason.runCells.explain.stopped', {
    need: pairs(row.need_units), target: say('inseason.runCells.explain.target', { target: pairs(target) }), position, reason,
  });
}

// ── the files ───────────────────────────────────────────────────────────────

/** One row of the files: a size not sent, with its reason and explanation already in words. */
export interface NotSentFileRow {
  readonly row: UncoveredReportRow;
  readonly reason: string;
  readonly explanation: string;
  readonly evidence: string | null;
}

export const NOT_SENT_EXPORT_ID = 'inseason.notSent.table';
export const NOT_SENT_TITLE_KEY = 'inseason.notSent.exportTitle';

/** The files' columns, in order: the owner's list (store, article, size, need, committed, target, reason, explanation), then the evidence. */
export function notSentColumns(mixedScales: boolean): readonly DataColumn<NotSentFileRow>[] {
  const fileOnly = { cell: () => null } as const;
  return [
    { id: 'store_key', headerKey: 'inseason.column.store', descriptionKey: 'inseason.help.notSent.storeKey', kind: 'text', value: ({ row }) => row.store_key, ...fileOnly },
    { id: 'store_name', headerKey: NAME_HEADER_KEY.store, descriptionKey: 'inseason.help.names.storeName', kind: 'text', value: ({ row }) => row.store_name, ...fileOnly },
    { id: 'article_key', headerKey: 'inseason.column.article', descriptionKey: 'inseason.help.notSent.articleKey', kind: 'text', value: ({ row }) => row.article_key, ...fileOnly },
    { id: 'article_name', headerKey: NAME_HEADER_KEY.article, descriptionKey: 'inseason.help.names.articleName', kind: 'text', value: ({ row }) => row.article_name, ...fileOnly },
    {
      id: 'size', headerKey: 'inseason.notSent.column.size', descriptionKey: 'inseason.help.notSent.size', kind: 'text',
      value: ({ row }) => sizeFilterValue(sizeName(row), mixedScales), sortValue: ({ row }) => sizeSortValue(sizeName(row)), ...fileOnly,
    },
    { id: 'need_units', headerKey: 'inseason.notSent.column.need', descriptionKey: 'inseason.help.notSent.need', kind: 'number', value: ({ row }) => row.need_units, ...fileOnly },
    { id: 'committed_units', headerKey: 'inseason.notSent.column.committed', descriptionKey: 'inseason.help.notSent.committed', kind: 'number', value: ({ row }) => row.committed_units, ...fileOnly },
    { id: 'target_units', headerKey: 'inseason.notSent.column.target', descriptionKey: 'inseason.help.notSent.target', kind: 'number', value: ({ row }) => row.target_units, ...fileOnly },
    { id: 'reason_code', headerKey: 'inseason.notSent.column.reasonCode', descriptionKey: 'inseason.help.notSent.reasonCode', kind: 'text', value: ({ row }) => row.uncovered_reason, ...fileOnly },
    { id: 'reason', headerKey: 'inseason.notSent.column.reason', descriptionKey: 'inseason.help.notSent.reason', kind: 'text', value: ({ reason }) => reason, ...fileOnly },
    { id: 'explanation', headerKey: 'inseason.notSent.column.explanation', descriptionKey: 'inseason.help.notSent.explanation', kind: 'text', value: ({ explanation }) => explanation, ...fileOnly },
    { id: 'evidence', headerKey: 'inseason.notSent.column.evidence', descriptionKey: 'inseason.help.notSent.evidence', kind: 'text', value: ({ evidence }) => evidence, ...fileOnly },
    { id: 'evidence_kind', headerKey: 'inseason.notSent.column.evidenceKind', descriptionKey: 'inseason.help.notSent.evidenceKind', kind: 'text', value: ({ row }) => row.evidence_kind ?? null, ...fileOnly },
    { id: 'evidence_currency_code', headerKey: 'inseason.notSent.column.currency', descriptionKey: 'inseason.help.notSent.currency', kind: 'text', value: ({ row }) => row.evidence_currency_code ?? null, ...fileOnly },
    { id: 'margin_per_pair', headerKey: 'inseason.notSent.column.margin', descriptionKey: 'inseason.help.notSent.margin', kind: 'number', value: ({ row }) => row.margin_per_pair ?? null, ...fileOnly },
    { id: 'cutoff_margin_per_pair', headerKey: 'inseason.notSent.column.cutoff', descriptionKey: 'inseason.help.notSent.cutoff', kind: 'number', value: ({ row }) => row.cutoff_margin_per_pair ?? null, ...fileOnly },
    { id: 'rank_position', headerKey: 'inseason.notSent.column.rank', descriptionKey: 'inseason.help.notSent.rank', kind: 'number', value: ({ row }) => row.rank_position ?? null, ...fileOnly },
    { id: 'candidate_count', headerKey: 'inseason.notSent.column.candidates', descriptionKey: 'inseason.help.notSent.candidates', kind: 'number', value: ({ row }) => row.candidate_count ?? null, ...fileOnly },
    { id: 'selected_count', headerKey: 'inseason.notSent.column.selected', descriptionKey: 'inseason.help.notSent.selected', kind: 'number', value: ({ row }) => row.selected_count ?? null, ...fileOnly },
    { id: 'budget_units', headerKey: 'inseason.notSent.column.budget', descriptionKey: 'inseason.help.notSent.budget', kind: 'number', value: ({ row }) => row.budget_units ?? null, ...fileOnly },
    { id: 'spent_units', headerKey: 'inseason.notSent.column.spent', descriptionKey: 'inseason.help.notSent.spent', kind: 'number', value: ({ row }) => row.spent_units ?? null, ...fileOnly },
    { id: 'cost_units', headerKey: 'inseason.notSent.column.cost', descriptionKey: 'inseason.help.notSent.cost', kind: 'number', value: ({ row }) => row.cost_units ?? null, ...fileOnly },
    { id: 'pool_units', headerKey: 'inseason.notSent.column.pool', descriptionKey: 'inseason.help.notSent.pool', kind: 'number', value: ({ row }) => row.pool_units ?? null, ...fileOnly },
    { id: 'left_units', headerKey: 'inseason.notSent.column.left', descriptionKey: 'inseason.help.notSent.left', kind: 'number', value: ({ row }) => row.left_units ?? null, ...fileOnly },
    { id: 'ahead_count', headerKey: 'inseason.notSent.column.ahead', descriptionKey: 'inseason.help.notSent.ahead', kind: 'number', value: ({ row }) => row.ahead_count ?? null, ...fileOnly },
    { id: 'missing_inputs', headerKey: 'inseason.notSent.column.missingInputs', descriptionKey: 'inseason.help.notSent.missingInputs', kind: 'text', value: ({ row }) => row.missing_inputs ?? null, ...fileOnly },
  ];
}

/** The rows of the uncovered report as file rows, their words in the reader's language. Pure. */
export function notSentFileRows(rows: readonly UncoveredReportRow[], t: Translate, language: string): readonly NotSentFileRow[] {
  const say = sentences(t);
  return rows.map((row) => {
    const reason = notSentReasonWords(row.uncovered_reason, row.missing_inputs, t, language, row.committed_units, null);
    return {
      row, reason, explanation: notSentExplanation(row, reason, language, say), evidence: evidenceSentence(row, t, language, row.uncovered_reason),
    };
  });
}

/** The files of the sizes not sent: the rows given, every column. */
export function notSentRequest(rows: readonly UncoveredReportRow[], t: Translate, language: string): ExportRequest {
  const fileRows = notSentFileRows(rows, t, language);
  return tableRequest({
    id: NOT_SENT_EXPORT_ID, titleKey: NOT_SENT_TITLE_KEY, columns: notSentColumns(scalesMix(rows.map(sizeName))), rows: fileRows,
    t: t as Parameters<typeof tableRequest>[0]['t'],
  });
}

/** The platform's cap on keys a filter may name (`MAXIMUM_NAME_KEYS`): above it the run is read whole and kept here. */
const MAXIMUM_FILTER_KEYS = 100;

/**
 * The list's Store and Article filters, as the platform takes them: a pair is kept when its store is
 * among `stores` AND its article among `articles`; an empty list keeps every value.
 */
export interface NotSentNarrowing {
  readonly stores: readonly string[];
  readonly articles: readonly string[];
}

/** No filter: the whole run. */
export const WHOLE_RUN: NotSentNarrowing = { stores: [], articles: [] };

/** The narrowing as query parameters — none for an empty list, so an unfiltered read names no key. Pure. */
export function narrowingQuery(narrowing: NotSentNarrowing): { store_key?: readonly string[]; article_key?: readonly string[] } {
  return {
    ...(narrowing.stores.length === 0 ? {} : { store_key: narrowing.stores }),
    ...(narrowing.articles.length === 0 ? {} : { article_key: narrowing.articles }),
  };
}

/**
 * Every size not sent of the pairs the filters keep (the whole run for {@link WHOLE_RUN}), read page by
 * page from the uncovered report — ONLY for a file, when a planner presses a download button (the walk
 * reports its pages to the download's progress, `@/api` walk-progress). The stores and articles are named
 * to the platform when there are few enough; past the platform's cap the run is read whole and kept here,
 * so a file holds exactly the list's rows.
 */
export async function notSentRows(runId: string, narrowing: NotSentNarrowing = WHOLE_RUN): Promise<readonly UncoveredReportRow[]> {
  const narrow = narrowing.stores.length <= MAXIMUM_FILTER_KEYS && narrowing.articles.length <= MAXIMUM_FILTER_KEYS;
  const rows = await collectPages(uncoveredReport, {
    path: { run_id: runId },
    query: { unserved_need_only: true, ...(narrow ? narrowingQuery(narrowing) : {}) },
  });
  if (narrow) return rows;
  const stores = new Set(narrowing.stores);
  const articles = new Set(narrowing.articles);
  return rows.filter((row) => (stores.size === 0 || stores.has(row.store_key)) && (articles.size === 0 || articles.has(row.article_key)));
}

/** The export census's resolver: the whole run's sizes not sent, for the page's `?runId=`. */
export async function notSentExport(scope?: ExportScope): Promise<ExportRequest> {
  const { runId } = readScope(scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
  const t: Translate = (key, values) => i18next.t(key, values ?? {});
  const rows = runId === null ? [] : await notSentRows(runId, WHOLE_RUN);
  // The handover causes among the reasons are the platform's words (`./network-health/pull-reasons.ts`).
  await primePullReasonLabels(i18next.language);
  return notSentRequest(rows, t, i18next.language);
}
